import React, { useMemo } from 'react';
import { Col } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import NotAvailable from 'components/NotAvailable';
import { CompanyTag, CompanySubtitle2, CompanyBody1 } from './atoms';

const I18N_BASE_PATH = 'pages.private.KYBCompany.pages';
const STATUS_PATH =
  'pages.private.KYBCompany.pages.companySearching.components.consultedCompanies.companyCard';
const SUMMARY = `${I18N_BASE_PATH}.companyDetails.subPages.summary`;
const INFO = `${SUMMARY}.cards.companyIdentificationInformation`;

export const CompanyContent = ({ i18n, title, text, col }) => {
  const { t: translate } = useTranslation();

  return (
    <Col xs={24} lg={col}>
      <CompanySubtitle2 level={5}>{translate(`${i18n}.${title}`)}</CompanySubtitle2>
      <CompanyBody1>{text}</CompanyBody1>
    </Col>
  );
};

export const CompanyArrayContent = ({
  i18n,
  title,
  fields,
  col,
  count,
  value = null,
  fieldsToShow = 2,
  extraInfo = null,
}) => {
  const { t: translate } = useTranslation();
  return (
    <Col xs={24} lg={col}>
      <CompanySubtitle2 level={5}>
        {translate(`${i18n}.${title}`)} ({count || '0'})
      </CompanySubtitle2>
      {!count && <NotAvailable />}
      {fields?.slice(0, fieldsToShow).map((field) => (
        <p>
          <CompanyBody1>{value ? field[value] : field}</CompanyBody1>
          {extraInfo ? <CompanyBody1> - {field[extraInfo]}%</CompanyBody1> : null}
        </p>
      ))}
      {count > fieldsToShow ? <p>+{count - fieldsToShow}</p> : <></>}
    </Col>
  );
};

export const CompanyTagging = ({ children, value, ...rest }) => {
  const text = (value?.toString() || '').toLowerCase();
  const forceOtherLettersAsSuccessEvenWithoutKnowing = ['b', 'c', 'd', 'e', 'f'];
  let className = ['low', 'a', ...forceOtherLettersAsSuccessEvenWithoutKnowing].includes(
    text
  )
    ? 'ant-tag-success'
    : 'ant-tag-warning';

  if (text.search(/active/gi) > -1) className = 'ant-tag-success';
  if (text.search(/inactive/gi) > -1) className = 'ant-tag-error';
  if (text.search(/medium/gi) > -1) className = 'ant-tag-warning';
  if (text.search(/low/gi) > -1) className = 'ant-tag-success';
  if (text.search(/critical/gi) > -1 || text.search(/high/gi) > -1) {
    className = 'ant-tag-error';
  }

  if (value < 40) className = 'ant-tag-success';
  if (value >= 40) className = 'ant-tag-warning';
  if (value >= 60) className = 'ant-tag-error';

  return (
    <CompanyTag className={className} {...rest}>
      {children}
    </CompanyTag>
  );
};

export const CompanyFinancialRiskTagging = ({ children, i18n, value, ...rest }) => {
  if (!value || value === '-') {
    return <CompanyTag empty />;
  }
  return (
    <CompanyTagging i18n={i18n} value={value} {...rest}>
      {children}
    </CompanyTagging>
  );
};

export const CompanyRiskFactorTagging = ({ i18n, value }) => {
  const { t } = useTranslation();
  let value0Until100 = 100;
  let title = value?.toString();
  if (typeof value === 'boolean') {
    value0Until100 = value ? 100 : 0;
  } else if (typeof value === 'number') {
    value0Until100 = value * 10 > 100 ? value : value * 10;
    title = value0Until100;
  } else {
    return <NotAvailable />;
  }
  let text = 'low';
  if (value0Until100 >= 25) text = 'moderate';
  if (value0Until100 >= 40) text = 'elevated';
  if (value0Until100 >= 60) text = 'high';
  if (value0Until100 >= 85) text = 'critical';
  return (
    <CompanyTagging value={value0Until100} title={title}>
      {t(`${i18n}.${text}`, '') || <NotAvailable />}
    </CompanyTagging>
  );
};

export const CompanyStatus = ({ i18n, title, text, col }) => {
  const { t: translate } = useTranslation();
  const companyStatus = () => {
    const status = text.toLowerCase();

    switch (status) {
      case 'active':
        return {
          description: translate(`${STATUS_PATH}.status.active`, 'Active'),
          value: 'active',
        };
      case 'nonactive':
      case 'closed':
      case 'expired':
      case 'inactive':
      case 'terminated':
        return {
          description: translate(`${STATUS_PATH}.status.inactive`, 'Inactive'),
          value: 'inactive',
        };
      case 'pending':
      case 'other':
      case 'closing':
      case 'expanded':
      case 'incorporated':
      case 'registered':
      case 'under_external_control':
        return {
          description: translate(`${STATUS_PATH}.status.other`, 'Other'),
          value: 'other',
        };
      default:
        return {
          description: '-',
          color: '',
        };
    }
  };

  return (
    <Col xs={24} lg={col}>
      <CompanySubtitle2>{translate(`${INFO}.${title}`)}</CompanySubtitle2>
      {companyStatus()?.value ? (
        <CompanyTagging value={companyStatus()?.value}>
          {companyStatus()?.description}
        </CompanyTagging>
      ) : (
        <NotAvailable />
      )}
    </Col>
  );
};

export const CompanyAddress = ({ i18n, title, text, col }) => {
  const { t: translate } = useTranslation();

  return (
    <Col xs={24} lg={col}>
      <CompanySubtitle2>{translate(`${i18n}.${title}`)}</CompanySubtitle2>
      <CompanyBody1>{text}</CompanyBody1>
    </Col>
  );
};

const propTypes = {
  i18n: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  col: PropTypes.string.isRequired,
};
const propTypesTagging = {
  value: PropTypes.string.isRequired,
};
const propTypesRiskTagging = {
  i18n: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

CompanyArrayContent.propTypes = {
  i18n: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  col: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  value: PropTypes.string,
  fieldsToShow: PropTypes.number,
  extraInfo: PropTypes.string,
};

CompanyArrayContent.defaultProps = {
  value: null,
  fieldsToShow: 2,
  extraInfo: null,
};

CompanyStatus.propTypes = propTypes;
CompanyTagging.propTypes = propTypesTagging;
CompanyRiskFactorTagging.propTypes = propTypesRiskTagging;
CompanyFinancialRiskTagging.propTypes = propTypesRiskTagging;
CompanyAddress.propTypes = propTypes;
CompanyContent.propTypes = propTypes;
